import NiceModal, {useModal} from "@ebay/nice-modal-react";
import {CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle} from "@mui/material";
import {Box, Colors, Container, Text} from "@bounce/shared/ui";
import {Button} from "../../ui/components";
import ResourceList from "../../components/ResourceList";
import {note, openInNewTab} from "../../components/ResourcePanel";
import DeleteResourceDialog from "../../components/DeleteResourceDialog";
import {reachUrls} from "../../layout/menu-items";
import {ReachAsset} from "../../apis/requests/assets/types";
import {plural, usageCount, usageSummary} from "../../apis/requests/resources/types";
import {useGetAssetsUsages} from "../../apis/requests/assets/getAssetsUsages";
import {useBatchDeleteAssets} from "../../apis/requests/assets/batchDeleteAssets";
import {toast} from "sonner";

type Props = {assets: ReachAsset[]; onDeleted?: () => void};

const BatchDeleteAssetsDialog = NiceModal.create(({assets, onDeleted}: Props) => {
  const modal = useModal();
  const {
    data: usages,
    isError: checkFailed,
    refetch: refreshUsages,
  } = useGetAssetsUsages({assetIds: assets.map((asset) => asset.id)});
  const {mutateAsync: deleteAssets, isPending: isDeleting} = useBatchDeleteAssets();

  const blocked = assets.filter((asset) => usageCount(usages?.[asset.id]) > 0);
  const free = assets.filter((asset) => usageCount(usages?.[asset.id]) === 0);

  const onReplace = (asset: ReachAsset) => {
    NiceModal.show(DeleteResourceDialog, {
      kind: "asset",
      mode: "replace",
      resourceId: asset.id,
      resourceName: asset.name,
      onClosed: () => void refreshUsages(),
    });
  };

  const onDelete = async () => {
    try {
      await deleteAssets({assetIds: free.map((asset) => asset.id)});
      toast.success(`${plural(free.length, "image")} deleted`);
      onDeleted?.();
      modal.hide();
    } catch {}
  };

  return (
    <Dialog
      open={modal.visible}
      onClose={() => modal.hide()}
      slotProps={{transition: {onExited: () => modal.remove()}, paper: {sx: {borderRadius: "16px"}}}}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{p: 3, pb: 0}}>
        <Text size={24} variant="interDisplay600">
          {assets.length === 1 ? "Delete image" : `Delete ${plural(assets.length, "image")}`}
        </Text>
      </DialogTitle>
      <DialogContent sx={{p: 3}}>
        <Container fullWidth sx={{gap: 2, mt: 1, alignItems: "flex-start"}}>
          {!usages && !checkFailed ? (
            <Box sx={{display: "flex", justifyContent: "center", width: "100%", py: 2}}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              <Text size={14} variant="inter400" sx={{color: Colors.grey._900, textAlign: "left"}}>
                {checkFailed
                  ? "Could not check where these images are used."
                  : blocked.length === 0
                    ? assets.length === 1
                      ? `"${assets[0].name}" is not used anywhere.`
                      : `${plural(assets.length, "image")} are not used anywhere.`
                    : blocked.length === 1 && assets.length === 1
                      ? `"${blocked[0].name}" is still in use and cannot be deleted yet.`
                      : `${plural(blocked.length, "image")} ${
                          blocked.length === 1 ? "is" : "are"
                        } still in use and cannot be deleted yet.`}
              </Text>
              {note(checkFailed
                  ? "Close this and try again."
                  : blocked.length === 0
                    ? `Deleting ${assets.length === 1 ? "it" : "them"} cannot be undone.`
                    : free.length === 0
                      ? `Replace ${blocked.length === 1 ? "it" : "them"} everywhere first, then ${
                          blocked.length === 1 ? "it" : "they"
                        } can be deleted.`
                      : "Delete removes only the images that are not in use. Replace the rest first to delete them too."
              )}

              {blocked.length > 0 && (
                <Container fullWidth sx={{gap: 1, alignItems: "flex-start"}}>
                  {note("Still in use:")}
                  <Container fullWidth sx={{gap: 0.75, alignItems: "stretch"}}>
                    {blocked.map((asset) => (
                      <Container
                        key={asset.id}
                        row
                        fullWidth
                        sx={{justifyContent: "space-between", alignItems: "center", gap: 1}}
                      >
                        <Text size={13} variant="inter400" sx={{color: Colors.grey._900, textAlign: "left"}}>
                          {asset.name} - {usageSummary(usages?.[asset.id])}
                        </Text>
                        <Button buttonType="outlined-secondary" size="small" onClick={() => onReplace(asset)}>
                          Replace
                        </Button>
                      </Container>
                    ))}
                  </Container>
                </Container>
              )}

              {free.length > 0 && assets.length > 1 && (
                <Container fullWidth sx={{gap: 1, alignItems: "flex-start"}}>
                  {note("Will be deleted:")}
                  <ResourceList
                    items={free.map((asset) => ({
                      id: String(asset.id),
                      label: asset.name,
                      onClick: () => openInNewTab(`${reachUrls.assets}?search=${encodeURIComponent(asset.url)}`),
                    }))}
                  />
                </Container>
              )}
            </>
          )}
        </Container>
      </DialogContent>
      <DialogActions sx={{p: 3, pt: 0, justifyContent: "flex-end", gap: 1, flexWrap: "wrap"}}>
        <Button buttonType="outlined-secondary" size="small" onClick={() => modal.hide()} disabled={isDeleting}>
          Cancel
        </Button>
        <Button
          buttonType="contained-error"
          size="small"
          onClick={onDelete}
          disabled={isDeleting || !usages || checkFailed || free.length === 0}
        >
          {blocked.length > 0 && free.length > 0 ? `Delete ${plural(free.length, "image")}` : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default BatchDeleteAssetsDialog;
